import DeleteModal from "components/shared/DeleteModal";
import ModalBase from "components/shared/ModalBase";
import { Spinner } from "components/shared/Spinner";
import StyledButton from "components/shared/StyledButton";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "store/store";
import type { TermTemplate } from "store/termTemplatesStore";
import { deleteTermTemplate, fetchAllTermTemplates } from "store/termTemplatesStore";

import styles from "./style.module.scss";
import TemplateBlock from "./TemplateBlock";
import TermTemplateForm from "./TermTemplateForm";

// Settings page for creating term templates.
const TermTemplates = () => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const { termTemplates, templatesStatus } = useAppSelector((state) => state.termTemplates);

    const [modalType, setModalType] = useState("");
    const [formMode, setFormMode] = useState<"new" | "edit">("new");
    const [chosenTemplate, setChosenTemplate] = useState<TermTemplate | undefined>(undefined);

    const handleDelete = async () => {
        if (chosenTemplate) {
            const response = await dispatch(deleteTermTemplate({ templateId: chosenTemplate?.id }));
            if (response.meta.requestStatus === "fulfilled") {
                setModalType("");
            }
        }
    };

    const handleEdit = (template: TermTemplate) => {
        if (chosenTemplate?.id === template.id) {
            setChosenTemplate(template);
            setModalType("edit");
            setFormMode("edit");
        }
    };

    useEffect(() => {
        if (!termTemplates.length) {
            dispatch(fetchAllTermTemplates({ beneficiaryId: null }));
        }
    }, []);

    return (
        <div className={styles["term-templates-container"]}>
            <div className={styles["page-header"]}>
                <div className={styles["header-title"]}>
                    <h1>{t("TermTemplates.TermTemplates")}</h1>
                    <StyledButton
                        buttonType="primary"
                        onClick={() => {
                            setModalType("create"), setFormMode("new");
                        }}
                    >
                        {t("TermTemplates.NewTypology")}
                    </StyledButton>
                </div>
                <span>{t("TermTemplates.SettingsSubtitle")}</span>
            </div>

            <div className={styles["templates-list"]}>
                {templatesStatus === "pending" && (
                    <div className={styles["spinner-container"]}>
                        <Spinner />
                        <span className={styles["loading-info"]}>{t("TermTemplates.LoadingTemplates")}</span>
                    </div>
                )}
                {termTemplates.map((template) => {
                    return (
                        <TemplateBlock
                            key={template.id}
                            template={template}
                            isOpen={chosenTemplate?.id === template.id}
                            setChosenTemplate={setChosenTemplate}
                            handleDelete={() => setModalType("delete")}
                            handleEdit={handleEdit}
                        />
                    );
                })}
            </div>
            <ModalBase
                open={["create", "edit"].includes(modalType)}
                onClose={() => setModalType("")}
                modalTitle={
                    modalType === "create"
                        ? t("TermTemplates.CreateTemplateTitle")
                        : t("TermTemplates.EditTemplateTitle")
                }
                modalSubtitle={t("TermTemplates.FormSubtitle")}
                widthTransition={{ width: "700px", active: true }}
                contentStyle={{ paddingInline: "0px", paddingBlock: "24px 0px" }}
            >
                <TermTemplateForm mode={formMode} onClose={() => setModalType("")} template={chosenTemplate} />
            </ModalBase>
            <DeleteModal
                open={modalType === "delete"}
                onClose={() => setModalType("")}
                onDelete={handleDelete}
                elementTitle={t("TermTemplates.TheTemplate", { templateName: chosenTemplate?.name ?? "" })}
            />
        </div>
    );
};

export default TermTemplates;
